import { RouteEnum } from 'core/enums/route.enum';
import { userManager } from './oidc.config';
import { persistor } from 'store';

//--------------------------------------------------------- OIDC Services ------------------------------------------------------

export const login = () => userManager.signinRedirect();

export const loginCallback = () => userManager.signinRedirectCallback();

export const silentLogin = () => userManager.signinSilent();

export const signOut = () => userManager.signoutRedirect();

export const getUser = () => userManager.getUser();

//--------------------------------------------------------- renewAccessToken ------------------------------------------------------

let renewPromise: Promise<any> | null = null;

export const renewAccessToken = async () => {
  if (renewPromise) return renewPromise;

  renewPromise = (async () => {
    try {
      const user = await userManager.signinSilent();
      return user;
    } catch (err) {
      throw err;
    } finally {
      renewPromise = null;
    }
  })();

  return renewPromise;
};

//--------------------------------------------------------- getAccessToken ------------------------------------------------------

export const getAccessToken = async (): Promise<string | null> => {
  const user = await userManager.getUser();
  return user?.access_token ?? null;
};

//--------------------------------------------------------- initAuthEvents ------------------------------------------------------

export const initAuthEvents = () => {
  userManager.events.addAccessTokenExpiring(async () => {
    try {
      console.log('Token expiring → attempting renew');

      await renewAccessToken();
    } catch (err) {
      console.warn('Silent renew failed', err);
    }
  });

  userManager.events.addAccessTokenExpired(async () => {
    console.warn('Token expired → forcing logout');

    await forceLogout();
  });
};

//--------------------------------------------------------- RedirectUrl ------------------------------------------------------
const REDIRECT_KEY = 'redirectAfterLogin';
const AUTH_PATHS: string[] = [RouteEnum.Login, RouteEnum.SignIn, RouteEnum.SignOut];

type TRedirectTarget = { path: string; state?: unknown };

export const saveCurrentLocation = () => {
  const { pathname, search, hash } = window.location;
  if (AUTH_PATHS.includes(pathname)) return;

  const target: TRedirectTarget = {
    path: `${pathname}${search}${hash}`,
    state: window.history.state?.usr,
  };

  try {
    sessionStorage.setItem(REDIRECT_KEY, JSON.stringify(target));
  } catch {
    sessionStorage.setItem(REDIRECT_KEY, JSON.stringify({ path: target.path }));
  }
};

export const getRedirectUrl = (): TRedirectTarget | null => {
  try {
    const raw = sessionStorage.getItem(REDIRECT_KEY);
    if (!raw) return null;
    const target = JSON.parse(raw) as TRedirectTarget;

    if (!target.path?.startsWith('/') || target.path.startsWith('//')) return null;
    return target;
  } catch {
    return null;
  }
};

export const removeRedirectUrl = () => sessionStorage.removeItem(REDIRECT_KEY);

export const redirectToSignOut = () => {
  saveCurrentLocation();
  window.location.pathname = RouteEnum.SignOut;
};

export const redirectToLogin = async () => {
  saveCurrentLocation();
  await persistor.purge();
  window.location.pathname = RouteEnum.Login;
};

//--------------------------------------------------------- handleUnauthorized ------------------------------------------------------
export const handleUnauthorized = async () => {
  const user = await userManager.getUser();

  if (!user) {
    await redirectToLogin();
    return;
  }

  redirectToSignOut();
};

//--------------------------------------------------------- forceLogout ------------------------------------------------------
export const forceLogout = async () => {
  saveCurrentLocation();
  const user = await userManager.getUser();
  await userManager.removeUser();
  await persistor.purge();

  if (!user) {
    window.location.pathname = RouteEnum.Login;
    return;
  }
  signOut();
};
